import { useCallback, useEffect, useState } from 'react';
import {
  canOfferPwaInstall,
  isIosDevice,
  isPwaStandalone,
} from '../pwa/pwaInstallUtils';

/**
 * Android: captures beforeinstallprompt for a one-tap install.
 * iOS: no programmatic install — callers show Add to Home Screen steps.
 */
export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(() => {
    if (isPwaStandalone()) return false;
    // iOS: show the offer (guided) when not installed.
    if (isIosDevice()) return true;
    // Android/others: only show after we receive the native prompt event.
    return false;
  });

  useEffect(() => {
    if (isPwaStandalone()) {
      setVisible(false);
      return;
    }
    // Keep iOS visible; Android waits for the event.
    if (isIosDevice()) setVisible(true);
  }, []);

  useEffect(() => {
    if (isPwaStandalone()) {
      setVisible(false);
      return undefined;
    }

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      // Android native prompt is now available.
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const promptAndroidInstall = useCallback(async () => {
    if (!deferredPrompt) return 'unavailable';
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setVisible(false);
      setDeferredPrompt(null);
      return outcome;
    } catch {
      return 'error';
    }
  }, [deferredPrompt]);

  return {
    canShowInstall: visible && canOfferPwaInstall(),
    hasNativePrompt: Boolean(deferredPrompt),
    needsIosGuide: isIosDevice() && !deferredPrompt,
    promptAndroidInstall,
  };
}
